import { FC, useMemo, useState } from "react";
import { VerifiedIncomeChart } from "./components/VerifiedIncomeChart";
import { Loader } from "shared/components/Loader";
import { Card } from "shared/components/Card";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { DetailsModal } from "./components/DetailsModal";
import { VerifiedIncomeGraphData } from "./types";

type Props = {
  isLoading?: boolean;
  isError?: boolean;
  verifiedIncomeGraphData?: VerifiedIncomeGraphData[];
};

export const IncomeSummary: FC<Props> = ({
  verifiedIncomeGraphData,
  isLoading,
  isError
}) => {
  const [showModal, setShowModal] = useState(false);

  const lastTwelveMonthOrLess = useMemo(() => {
    return verifiedIncomeGraphData?.reduce((acc, item) => {
      const lastTwelveMonths = item.data.slice(-12);
      acc.push({ ...item, data: lastTwelveMonths });
      return acc;
    }, [] as VerifiedIncomeGraphData[]);
  }, [verifiedIncomeGraphData]);

  if (isLoading)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader />
      </div>
    );

  if (isError)
    return (
      <div className="flex flex-1 justify-center items-center">
        <p className="font-bold text-base text-content-500 mt-6">
          Error loading combined income graph
        </p>
      </div>
    );

  if (!verifiedIncomeGraphData?.length || !lastTwelveMonthOrLess?.length)
    return null;

  return (
    <>
      <div className="flex-1 md:block hidden">
        <Card className="min-h-[395px]">
          <h2 className="px-6 py-4 border-b border-alpha-40 text-base font-medium text-content-700">
            Group net income
          </h2>
          <div className="flex justify-end px-6 gap-4">
            {verifiedIncomeGraphData.map((line) => (
              <div key={line.id} className="min-w-max">
                <span
                  className="inline-block w-2 h-2 rounded-full mr-2"
                  style={{
                    backgroundColor: line.color
                  }}
                />
                <span className="text-xs text-content-1000">
                  {line.id}
                  <span className="text-content-200 text-xs">
                    {" "}
                    ({line.role})
                  </span>
                </span>
              </div>
            ))}
          </div>
          <div className="h-[278px] px-6">
            <VerifiedIncomeChart data={verifiedIncomeGraphData} />
          </div>
          <div className="border-t border-alpha-40 px-4 py-5 flex items-center justify-between">
            <Button
              className="mx-auto"
              variant={ButtonVariant.TEXT}
              size={ButtonSize.XS}
              onClick={() => setShowModal(true)}
            >
              Show more
            </Button>
          </div>
        </Card>
      </div>
      <DetailsModal
        opened={showModal}
        onClose={() => setShowModal(false)}
        verifiedIncomeGraphData={lastTwelveMonthOrLess}
      />
    </>
  );
};
